import React, { useState, useEffect, useContext } from 'react';
import { Save } from 'lucide-react';
import api from '../api';
import { AuthContext } from '../context/AuthContext';

const Settings = () => {
  const { updateUser } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    telegram_chat_id: '',
    annoying_interval_default: 10,
    annoying_limit_default: 3
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get('/users/settings');
        setFormData({
          telegram_chat_id: res.data.telegram_chat_id || '',
          annoying_interval_default: res.data.annoying_interval_default || 10,
          annoying_limit_default: res.data.annoying_limit_default || 3
        });
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch settings', err);
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (e) => {
    const value = e.target.name === 'telegram_chat_id' ? e.target.value : (parseInt(e.target.value) || 0);
    setFormData({ ...formData, [e.target.name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');

    try {
      await api.put('/users/settings', formData);
      updateUser({ telegram_chat_id: formData.telegram_chat_id });
      setMessage('Settings updated successfully! 🎉');
      setSaving(false);
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError('Failed to update settings');
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center mt-20 text-slate-400">Loading settings...</div>;

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in-up">
      <div>
        <h2 className="text-3xl font-bold text-white">Global Settings</h2>
        <p className="text-slate-400 mt-1">Configure your master preferences</p>
      </div>
      
      {error && (
        <div className="bg-red-500/10 border border-red-500/50 text-red-400 p-4 rounded-xl">
          {error}
        </div>
      )}

      {message && (
        <div className="bg-emerald-500/10 border border-emerald-500/50 text-emerald-400 p-4 rounded-xl">
          {message}
        </div>
      )}

      <div className="bg-slate-800/60 backdrop-blur-md rounded-2xl border border-slate-700/50 shadow-2xl overflow-hidden">
        <form className="p-6 sm:p-8 space-y-8" onSubmit={handleSubmit}>
          
          <div>
            <h3 className="text-xl font-bold text-white flex items-center space-x-2 mb-4">
              <span>🤖 Telegram Bot Integration</span>
            </h3>
            <p className="text-sm text-slate-400 mb-6">
              Enter your Telegram Chat ID to receive reminders directly on your phone.
            </p>

            <div className="bg-slate-900/40 p-5 rounded-2xl border border-slate-700/50 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">Telegram Chat ID</label>
                <input 
                  type="text" 
                  name="telegram_chat_id"
                  value={formData.telegram_chat_id}
                  onChange={handleChange}
                  placeholder="e.g. 6219748833"
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all" 
                />
                <p className="text-xs text-slate-500 mt-2 font-medium">
                  Don't know your ID?{' '}
                  <a href="https://t.me/Vikas_reminder_bot?start=1" target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:text-indigo-300 transition-colors">
                    Click here to message @Vikas_reminder_bot
                  </a>{' '}
                  and it will send you your unique ID.
                </p>
              </div>
            </div>
          </div>

          <div className="pt-6 border-t border-slate-700/50">
            <h3 className="text-xl font-bold text-white flex items-center space-x-2 mb-4">
              <span>🚨 Annoying Mode Defaults</span>
            </h3>
            <p className="text-sm text-slate-400 mb-6">
              When you toggle "Annoying Mode" on a reminder, it will automatically use these escalation limits.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 bg-slate-900/40 p-5 rounded-2xl border border-slate-700/50">
              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">Interval (Minutes)</label>
                <input 
                  type="number" 
                  name="annoying_interval_default"
                  value={formData.annoying_interval_default}
                  onChange={handleChange}
                  min="1"
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all" 
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">Max Messages</label>
                <input 
                  type="number" 
                  name="annoying_limit_default"
                  value={formData.annoying_limit_default}
                  onChange={handleChange}
                  min="1"
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all" 
                />
              </div>
            </div>
          </div>

          <div className="pt-6 border-t border-slate-700/50">
            <button 
              type="submit" 
              disabled={saving}
              className="flex items-center justify-center space-x-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 disabled:opacity-50 text-white font-bold py-3 px-6 rounded-xl transition-all shadow-lg shadow-indigo-500/25 hover:-translate-y-0.5"
            >
              <span>{saving ? 'Saving...' : 'Save Settings'}</span>
              {!saving && <Save className="w-5 h-5" />}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Settings;
